import { useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { SidebarInsetContext } from '@/components/page/ContentWrapper';
import { HEADER_HEIGHT, NAV_WIDTH } from '@/constants';

/**
 * The height of the stuck row, measured: `BitkitPageHeader`'s collapsed block is `minHeight: 72` and
 * the row adds its 1px bottom border. Everything that sits under the stuck header offsets by this, so
 * it has to be what is actually there -- at 72 the sidebar's stuck list sat 7 below the row, not 16.
 */
export const PAGE_HEADER_STICKY_HEIGHT = 73;

type StickyPageHeaderProps = {
  /**
   * Called with the current sticky state. The stuck row and the in-flow header are separate mounts,
   * so anything stateful inside is remounted as the header collapses.
   */
  children: (isSticky: boolean) => ReactNode;
  onStickyChange?: (isSticky: boolean) => void;
  shouldSticky?: boolean;
};

/**
 * Owns the one copy of the page-header sticky behaviour: the scroll detection, the placeholder that
 * keeps the page from jumping as the header leaves the flow, and the portal that lifts the stuck row
 * into the platform header.
 *
 * It is a wrapper rather than a hook because the portal and the placeholder are what make it work,
 * and those are markup. `BitkitPageHeader`'s own `isSticky` is appearance-only by design — it
 * collapses the row and nothing else — so everything around it has to live on this side.
 *
 * Three things here are load-bearing outside this file, so they are deliberately in one place:
 * `PAGE_HEADER_STICKY_HEIGHT` feeds the scroll offsets in the log viewer, the sidebar height hooks and
 * the comparison cards; `.sticky-page-header` is measured from the DOM by `LogViewerToolbar`; and
 * `.platform-header` is the portal target rendered by `UIHeader`.
 */
export default function StickyPageHeader({ children, onStickyChange, shouldSticky }: StickyPageHeaderProps) {
  const { isMobile } = useResponsive();
  const sidebarInset = useContext(SidebarInsetContext);

  const platformHeader: HTMLElement | null = document.querySelector('.platform-header');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const [hasScrolledPast, setHasScrolledPast] = useState(false);

  // Derived rather than reset from the effect: when sticking is switched off, or the viewport drops
  // to mobile, the header has to be back in the flow on that same render — not one effect later.
  // The portal target is part of it too, so `onStickyChange` never reports a row that isn't rendered.
  const isSticky = Boolean(shouldSticky) && !isMobile && hasScrolledPast && platformHeader !== null;

  useEffect(() => {
    if (!shouldSticky || isMobile) {
      return undefined;
    }

    const ref = containerRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        setHasScrolledPast(!entries[0].isIntersecting);
      },
      {
        rootMargin: `-${HEADER_HEIGHT + PAGE_HEADER_STICKY_HEIGHT}px`,
      },
    );

    if (ref) {
      observer.observe(ref);
    }

    return () => {
      observer.disconnect();
      // Otherwise a scroll position from before a trip to mobile would stick the header for the
      // one render before the next observer reports.
      setHasScrolledPast(false);
    };
  }, [isMobile, shouldSticky]);

  useEffect(() => {
    onStickyChange?.(isSticky);
  }, [isSticky, onStickyChange]);

  // Measured once, before anything can go sticky, so the reserved height is the expanded one.
  useEffect(() => {
    setPlaceholderHeight(containerRef?.current?.offsetHeight || 0);
  }, []);

  return (
    <Box ref={containerRef} minHeight={['initial', placeholderHeight]}>
      {isSticky && platformHeader
        ? ReactDOM.createPortal(
            // The row's box starts at the platform nav's edge, so its background and shadow meet it;
            // BitkitPageHeader's own 24 start padding puts the content where the in-flow header has it.
            // A header rendered inside a sidebar layout's content column offsets by the sidebar too,
            // so the stuck row keeps the column's edges -- the sidebar and its title stay visible.
            // The row's look comes from BitkitPageHeader's `isSticky`.
            // `100%` rather than no offset at all: the header's root is a flex column, and an
            // absolutely positioned child of a flex container starts at the container's content box
            // instead of after its in-flow siblings -- without it the row covers the bar. It is the
            // root's full height, so a banner below the bar pushes the row down with it.
            <Box
              className="sticky-page-header"
              data-testid="sticky-page-header"
              insetBlockStart="100%"
              paddingLeft={sidebarInset ? `calc(${NAV_WIDTH}px + ${sidebarInset})` : NAV_WIDTH}
              position="absolute"
              width="100%"
            >
              {children(true)}
            </Box>,
            platformHeader,
          )
        : children(false)}
    </Box>
  );
}
